import React from 'react';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DateInputField from '../../components/DateInputField';

export const defaultIncidentForm = {
  eventDate: '',
  title: '',
  description: '',
  injuryType: 'without_person_damage',
  entryType: '',
  responsibleId: '',
  reporterMode: 'self',
  reporterName: '',
  linksText: '',
};

function parseLinksText(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function getInitialIncidentFormState() {
  return { ...defaultIncidentForm };
}

export function isIncidentFormValid(formState) {
  const injuryTypeValid = ['without_person_damage', 'with_person_damage'].includes(formState?.injuryType);
  const reporterMode = String(formState?.reporterMode || '').trim();

  if (!String(formState?.eventDate || '').trim()) {
    return false;
  }

  if (!String(formState?.title || '').trim()) {
    return false;
  }

  if (!String(formState?.description || '').trim()) {
    return false;
  }

  if (!injuryTypeValid) {
    return false;
  }

  if (!String(formState?.entryType || '').trim()) {
    return false;
  }

  if (!Number.isInteger(Number(formState?.responsibleId)) || Number(formState?.responsibleId) <= 0) {
    return false;
  }

  if (!['self', 'other_named', 'anonymous'].includes(reporterMode)) {
    return false;
  }

  if (reporterMode === 'other_named' && !String(formState?.reporterName || '').trim()) {
    return false;
  }

  return true;
}

export function buildIncidentSubmitPayload(formState, options) {
  const responsible = (options?.responsibles || []).find(
    (item) => Number(item.id) === Number(formState?.responsibleId),
  );

  return {
    topicKey: 'zwischenfaelle',
    eventDate: String(formState?.eventDate || '').trim(),
    title: String(formState?.title || '').trim(),
    description: String(formState?.description || '').trim(),
    payload: {
      links: parseLinksText(formState?.linksText),
      injuryType: formState?.injuryType,
      entryType: String(formState?.entryType || '').trim(),
      responsibleId: Number(formState?.responsibleId),
      responsibleNameSnapshot: responsible?.full_name || null,
      reporterMode: formState?.reporterMode,
      ...(formState?.reporterMode === 'other_named'
        ? { reporterName: String(formState?.reporterName || '').trim() }
        : {}),
    },
  };
}

export function buildIncidentEditFormState(entry) {
  const payload = entry?.payload || {};

  return {
    eventDate: entry?.event_date || '',
    title: entry?.title || '',
    description: entry?.description || '',
    injuryType: payload.injuryType || 'without_person_damage',
    entryType: payload.entryType || '',
    responsibleId: payload.responsibleId || '',
    reporterMode: payload.reporterMode || 'self',
    reporterName: payload.reporterName || '',
    linksText: Array.isArray(payload.links) ? payload.links.join('\n') : '',
  };
}

function mergeFiles(existingFiles = [], nextFiles = []) {
  const merged = [...existingFiles];

  nextFiles.forEach((file) => {
    const duplicate = merged.some(
      (existing) => existing.name === file.name
        && existing.size === file.size
        && existing.lastModified === file.lastModified,
    );

    if (!duplicate) {
      merged.push(file);
    }
  });

  return merged;
}

export function FeedbackFileDropzone({
  label,
  accept,
  files = [],
  onFilesChange,
  buttonLabel,
  disabled = false,
}) {
  const inputId = React.useId();
  const [dragActive, setDragActive] = React.useState(false);

  const addFiles = React.useCallback((incomingFiles) => {
    if (disabled) {
      return;
    }
    const normalizedFiles = Array.from(incomingFiles || []);
    if (!normalizedFiles.length) {
      return;
    }

    onFilesChange(mergeFiles(files, normalizedFiles));
  }, [disabled, files, onFilesChange]);

  const handleDrop = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    addFiles(event.dataTransfer?.files || []);
  }, [addFiles]);

  const handleDragState = React.useCallback((event, nextState) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(nextState);
  }, []);

  const handleRemoveFile = React.useCallback((targetFile) => {
    if (disabled) {
      return;
    }
    onFilesChange(
      files.filter(
        (file) => !(file.name === targetFile.name && file.size === targetFile.size && file.lastModified === targetFile.lastModified),
      ),
    );
  }, [disabled, files, onFilesChange]);

  return (
    <Stack spacing={0.75}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Box
        sx={{
          border: '1px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          px: 2,
          py: 2,
          bgcolor: dragActive ? 'action.hover' : 'background.paper',
        }}
        onDragEnter={(event) => handleDragState(event, true)}
        onDragOver={(event) => handleDragState(event, true)}
        onDragLeave={(event) => handleDragState(event, false)}
        onDrop={handleDrop}
      >
        <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Dateien hierhin ziehen oder über den Button auswählen.
          </Typography>
          <Button variant="outlined" component="label">
            {buttonLabel}
            <input
              id={inputId}
              hidden
              type="file"
              multiple
              accept={accept}
              disabled={disabled}
              onChange={(event) => addFiles(event.target.files || [])}
            />
          </Button>
        </Stack>
      </Box>
      {files.length > 0 && (
        <Stack spacing={0.5}>
          {files.map((file) => (
            <Stack key={`${file.name}-${file.lastModified}-${file.size}`} direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {file.name}
              </Typography>
              <Button size="small" variant="text" color="error" onClick={() => handleRemoveFile(file)} disabled={disabled}>
                Entfernen
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export function IncidentFeedbackFormFields({
  formState,
  onFieldChange,
  options,
  descriptionLabel = 'Beschreibung des Zwischenfalls',
  linksLabel = 'URLs (eine pro Zeile)',
  attachmentFiles = [],
  onAttachmentFilesChange,
  showAssetPickers = true,
  disabled = false,
}) {
  return (
    <Stack spacing={1.25}>
      <DateInputField
        label="Datum"
        value={formState.eventDate}
        onChange={(value) => onFieldChange('eventDate', value)}
        helperText="Z. B. 14042026 oder 14.04.2026"
        fullWidth
        disabled={disabled}
      />

      <TextField
        label="Titel"
        value={formState.title}
        onChange={(event) => onFieldChange('title', event.target.value)}
        fullWidth
        disabled={disabled}
      />

      <ToggleButtonGroup
        exclusive
        value={formState.injuryType}
        onChange={(_, value) => {
          if (value) {
            onFieldChange('injuryType', value);
          }
        }}
        fullWidth
        disabled={disabled}
      >
        <ToggleButton value="without_person_damage">Ohne Personenschaden</ToggleButton>
        <ToggleButton value="with_person_damage">Mit Personenschaden</ToggleButton>
      </ToggleButtonGroup>

      <TextField
        select
        label="Art der Tätigkeit"
        value={formState.entryType}
        onChange={(event) => onFieldChange('entryType', event.target.value)}
        fullWidth
        disabled={disabled}
      >
        {(options?.entryTypes || []).map((entryType) => (
          <MenuItem key={entryType} value={entryType}>{entryType}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Verantwortlicher"
        value={formState.responsibleId}
        onChange={(event) => onFieldChange('responsibleId', event.target.value)}
        fullWidth
        disabled={disabled}
      >
        {(options?.responsibles || []).map((responsible) => (
          <MenuItem key={responsible.id} value={responsible.id}>{responsible.full_name}</MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Meldender"
        value={formState.reporterMode}
        onChange={(event) => onFieldChange('reporterMode', event.target.value)}
        fullWidth
        disabled={disabled}
      >
        <MenuItem value="self">Ich bin der Meldende</MenuItem>
        <MenuItem value="other_named">Andere benannte Person</MenuItem>
        <MenuItem value="anonymous">Meldender bleibt anonym</MenuItem>
      </TextField>

      {formState.reporterMode === 'other_named' && (
        <TextField
          label="Name des Meldenden"
          value={formState.reporterName}
          onChange={(event) => onFieldChange('reporterName', event.target.value)}
          fullWidth
          disabled={disabled}
        />
      )}

      <TextField
        label={descriptionLabel}
        value={formState.description}
        onChange={(event) => onFieldChange('description', event.target.value)}
        fullWidth
        multiline
        minRows={4}
        disabled={disabled}
      />

      <TextField
        label={linksLabel}
        value={formState.linksText}
        onChange={(event) => onFieldChange('linksText', event.target.value)}
        fullWidth
        multiline
        minRows={3}
        disabled={disabled}
      />

      {showAssetPickers && (
        <FeedbackFileDropzone
          label="Anhänge (Bilder, Videos & sonstiges)"
          accept="image/*,video/*,audio/*,text/*,application/*"
          files={attachmentFiles}
          onFilesChange={onAttachmentFilesChange}
          buttonLabel="Anhänge auswählen"
          disabled={disabled}
        />
      )}
    </Stack>
  );
}
